import React from "react";
import { Textfield } from "@natera/form";
import classNames from "classnames";
import { HeapPHIDecorator } from "@app/components/analytics";
import {
  SetValidationError,
  ClearValidationError,
} from "@natera/platform/lib/hooks/useError";
import { defineMessages, useIntl } from "react-intl";
import { NoScrollFormField } from "@app/components/ui/inputs/noScrollFormField";

const messages = defineMessages({
  dateOfBirthYearLabel: {
    id: "dateOfBirthVerificationYearLabel",
    defaultMessage: "Year",
  },
  dateOfBirthVerificationInvalidDob: {
    id: "dateOfBirthVerificationInvalidDob",
    defaultMessage: "Incorrect date of birth. Please re-enter",
  },
});

interface DobYearFieldProps {
  setYear: (year: string) => void;
  year: string;

  lowerYearLimit: number;
  currentYear: number;
  showLabel?: boolean;
  disabled?: boolean;

  getValidationError: (field: string) => string | undefined;
  setValidationError: SetValidationError;
  clearValidationError: ClearValidationError;
}

const DobYearField: React.FC<DobYearFieldProps> = ({
  setYear,
  year,
  lowerYearLimit,
  currentYear,
  showLabel = true,
  disabled,
  getValidationError,
  setValidationError,
  clearValidationError,
}) => {
  const intl = useIntl();

  const invalidYearMessage = intl.formatMessage(
    messages.dateOfBirthVerificationInvalidDob
  );

  const handleYearChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value) {
      clearValidationError("dateOfBirthYear");
      clearValidationError("dateOfBirthIncorrect");
      clearValidationError("dateOfBirthRequired");
    }

    let inputValue = event.target.value.replace(/\D/g, "");
    if (inputValue.length > 4) {
      inputValue = inputValue.slice(0, 4);
    }

    const numericValue = parseInt(inputValue, 10);

    setYear(inputValue);

    if (inputValue.length < 4) {
      return;
    }

    if (!isNaN(numericValue)) {
      if (numericValue < lowerYearLimit || numericValue > currentYear) {
        setValidationError("dateOfBirthYear", invalidYearMessage);
        return;
      } else {
        clearValidationError("dateOfBirth");
        clearValidationError("dateOfBirthYear");
        clearValidationError("dateOfBirthIncorrect");
      }
    }
  };

  return (
    <NoScrollFormField
      label={
        showLabel
          ? intl.formatMessage(messages.dateOfBirthYearLabel)
          : undefined
      }
      error={
        getValidationError("dateOfBirthRequired") ??
        getValidationError("dateOfBirthYear") ??
        getValidationError("dateOfBirthIncorrect")
      }
      htmlFor="dateOfBirthYear"
      className={classNames("dob-input__year", {
        "dob-verification-form__invalid":
          getValidationError("dateOfBirthIncorrect") ||
          getValidationError("dateOfBirthYear") ||
          getValidationError("dateOfBirthRequired"),
      })}
    >
      <HeapPHIDecorator protectAttr={["value"]} noWrap={true}>
        <Textfield
          placeholder={intl.formatMessage(messages.dateOfBirthYearLabel)}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          id="dateOfBirthYear"
          className="dob-input__input"
          maxLength={4}
          name="dateOfBirthYear"
          data-testid="dateOfBirthYear"
          outline
          value={year}
          onChange={handleYearChange}
          fullWidth
          disabled={disabled}
        />
      </HeapPHIDecorator>
    </NoScrollFormField>
  );
};

export default DobYearField;
